import { RefObject } from "react";

const MAX_FOCUS_ATTEMPTS = 4;
const MAX_REF_WAIT_ATTEMPTS = 10;
const RETRY_INTERVAL_MS = 50;

/**
 * Delay before marking a field touched after blur.
 * setTimeout(0) can still run before click completes in some browsers,
 * which steals the first click when validation errors re-render.
 */
export const TOUCHED_BLUR_DELAY_MS = 100;

export function deferTouchedAfterBlur(action: () => void): number {
    return window.setTimeout(action, TOUCHED_BLUR_DELAY_MS);
}

/**
 * Starts a retry loop that waits for the ref to become available,
 * then attempts to focus it. Returns a cleanup function that cancels
 * any pending timeout.
 */
export function autoFocusRef(
    ref: RefObject<HTMLElement | null>,
    onAutoFocused?: () => void
): () => void {
    let focusAttempts = 0;
    let refWaitAttempts = 0;
    let timeoutId = 0;

    const attemptFocus = () => {
        const target = ref.current;
        if (!target) {
            if (refWaitAttempts >= MAX_REF_WAIT_ATTEMPTS) {
                onAutoFocused?.();
                return;
            }
            refWaitAttempts += 1;
            timeoutId = window.setTimeout(attemptFocus, RETRY_INTERVAL_MS);
            return;
        }

        target.focus();
        const hasFocus =
            document.activeElement === target || target.matches(":focus");

        if (hasFocus || focusAttempts >= MAX_FOCUS_ATTEMPTS) {
            onAutoFocused?.();
            return;
        }

        focusAttempts += 1;
        timeoutId = window.setTimeout(attemptFocus, RETRY_INTERVAL_MS);
    };

    timeoutId = window.setTimeout(attemptFocus, 0);
    return () => window.clearTimeout(timeoutId);
}
